import { useTranslation } from 'react-i18next';
import { useCheckoutLinkPopup } from '../hooks/useCheckoutLinkPopup';
import { UserDetailsDto } from '../../../services/User/userService.dto';
import { useOnSiteOrder } from '../hooks/useOnSiteOrder';
import { AuctionDto } from '../../../services/Auction/auctionService.dto';
import { formatPrice } from '../../../utils/priceFormatter';
import RectangularButton from '../../../components/buttons/RectangularButton';
import LoadingIndicator from '../../../components/loading/LoadingIndicator';
import { OrderDto } from '../../../services/Order/orderService.dto';
import { TechnicalUserStatus } from '../hooks/useTechnicalUser';
import {
  DetailsGrid,
  DetailsTitle,
  DetailsValue,
  InfoHeader,
  SectionContainer,
  SectionLeftColumn,
  SectionRightColumn,
  WarningHeader,
} from './layoutComponents';

interface Props {
  refetchTechnicalUser: () => Promise<UserDetailsDto>;
  technicalUserStatus: TechnicalUserStatus;
  auction?: AuctionDto;
  technicalUser?: UserDetailsDto;
}

const OrderDetails = ({ auction, technicalUser, technicalUserStatus, refetchTechnicalUser }: Props) => {
  const { showCheckoutLinkPopup } = useCheckoutLinkPopup();
  const { order, orderStatus } = useOnSiteOrder(technicalUserStatus, auction, technicalUser);
  const { t } = useTranslation('onsite');

  const renderDetails = (order: OrderDto, technicalUser?: UserDetailsDto) => {
    return (
      <SectionContainer>
        <SectionLeftColumn>
          <DetailsGrid>
            <DetailsTitle>{`${t('order.order-id')}:`}</DetailsTitle>
            <DetailsValue>{order.id}</DetailsValue>
            <DetailsTitle>{`${t('order.base-price')}:`}</DetailsTitle>
            <DetailsValue>{formatPrice(order.basePrice)}</DetailsValue>
            <DetailsTitle>{`${t('order.amount-total')}:`}</DetailsTitle>
            <DetailsValue>{formatPrice(order.amountTotal)}</DetailsValue>
            <DetailsTitle>{`${t('order.order-status')}:`}</DetailsTitle>
            <DetailsValue>{order.status}</DetailsValue>
            <DetailsTitle>{`${t('order.buyer-id')}:`}</DetailsTitle>
            <DetailsValue>{order.buyerId}</DetailsValue>
          </DetailsGrid>
        </SectionLeftColumn>
        <SectionRightColumn>
          <RectangularButton
            disabled={orderStatus !== 'ORDER_READY'}
            label={t('order.show-checkout-link')}
            onClick={() => showCheckoutLinkPopup(order, technicalUser!!, refetchTechnicalUser, false)}
          />
          <RectangularButton
            disabled={orderStatus !== 'ORDER_READY'}
            label={t('order.reset-checkout-link')}
            onClick={() => showCheckoutLinkPopup(order, technicalUser!!, refetchTechnicalUser, true)}
          />
        </SectionRightColumn>
      </SectionContainer>
    );
  };

  const renderInfo = () => {
    if (orderStatus === 'AUCTION_IN_PROGRESS') {
      return <InfoHeader>{t('order.auction-in-progress')}</InfoHeader>;
    }

    if (orderStatus === 'WAITING_FOR_RESOLUTION') {
      return <InfoHeader>{t('order.waiting-for-resolution')}</InfoHeader>;
    }

    if (orderStatus === 'WINNER_IS_NOT_TECHNICAL') {
      return <InfoHeader>{t('order.winner-not-technical')}</InfoHeader>;
    }

    if (orderStatus === 'AUCTION_HAS_NO_WINNER') {
      return <InfoHeader>{t('order.auction-no-winner')}</InfoHeader>;
    }

    if (orderStatus === 'UNKNOWN_ERROR') {
      return <WarningHeader>{t('order.unknown-error')}</WarningHeader>;
    }

    return <LoadingIndicator />;
  };

  if ((orderStatus === 'ORDER_READY' || orderStatus === 'ORDER_NOT_ACTIVE') && !!order) {
    return renderDetails(order, technicalUser);
  } else {
    return <SectionContainer>{renderInfo()}</SectionContainer>;
  }
};

export default OrderDetails;
